import { isKioskShell } from '../config/kioskShell';
import { devWarn } from '../lib/devLog';

/**
 * MediaRecorder container selection, which is per-platform and then verified.
 *
 * On the web, MP4/H.264 leads: it is what iOS Safari produces and what the
 * share sheet, Photos and MMS accept without a re-encode. On the kiosk MP4 is
 * not an option at all — see WEBM_ONLY. And because `isTypeSupported` lies on
 * that hardware, the chosen container is confirmed by recording with it before
 * a guest ever depends on it — see probeMimeType.
 */

const MP4_FIRST = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4;codecs=avc1',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=h264,opus',
  'video/webm',
];

/**
 * The kiosk list. **MP4 is absent deliberately — do not add it back.**
 *
 * On the RK3568 panel, MediaRecorder claims MP4 support and then destroys the
 * page. Measured on the unit 2026-09-18, video-only and with audio:
 *
 *   video/webm (+ audio)  ->  14128 bytes, no error          works
 *   video/mp4  (no audio) ->  never fires onstop             hangs
 *   video/mp4  (+ audio)  ->  renderer process crash, code 5 kills the WebView
 *
 * The crash is recoverable — KioskWebViewClient rebuilds the WebView — but it
 * takes the guest's session with it, and `isTypeSupported` returns true for
 * every one of these, so nothing short of recording with it reveals the
 * difference. Even *probing* MP4 here is unsafe, which is why the list is
 * filtered rather than merely reordered.
 *
 * Consequence worth carrying: kiosk clips are WebM, so `smsFriendly()` is false
 * for them and the retrieval page must serve something an iPhone can play. That
 * needs a transcode somewhere — it is not solved here.
 *
 * VP8 leads, not VP9. Neither has a hardware encoder on this chip — the board
 * ships `OMX.rk.video_decoder.vp8/vp9` but only `OMX.rk.video_encoder.avc`, so
 * both are software encodes and encoding is what costs the live preview its
 * frame rate. VP8 is materially cheaper to encode than VP9 and there is nothing
 * here that VP9 buys.
 */
const WEBM_ONLY = [
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9,opus',
  'video/webm',
];

function candidates(): string[] {
  return isKioskShell() ? WEBM_ONLY : MP4_FIRST;
}

/**
 * What `probeMimeType()` proved actually works, once it has run.
 *
 * `isTypeSupported` is a claim, not a guarantee, and on the RK3568 kiosk panel
 * it is a false one: it answers true for `video/mp4;codecs=avc1.42E01E,mp4a.40.2`
 * and then MediaRecorder raises an error event partway through and emits a blob
 * of a few KB. Measured on the unit 2026-09-18 — every take came back
 * "0.01 MB" and the guest got "The recording did not come through".
 *
 * So the container is chosen by recording with it, not by asking about it.
 */
let proven: string | undefined;

export function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  // A container the probe actually proved outranks the static list. A probe
  // FAILURE does not: it falls through to the static pick below.
  //
  // That asymmetry is deliberate and was learned the hard way. The probe shares
  // a loaded CPU with the Camera Kit bootstrap at warm-up, and on the kiosk it
  // is flaky under that load — one run proved `video/webm`, the next produced
  // nothing. Treating "nothing" as authoritative disabled recording for the
  // whole session: no container, so `recorderSupported()` was false, the
  // pre-roll never started, and the guest would have reached the result screen
  // with no clip. A flaky probe must never be worse than no probe at all.
  if (proven) return proven;
  for (const type of candidates()) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return null;
}

/** Does this look like a real clip, or the few-KB stub a failing encoder emits? */
const PLAUSIBLE_BYTES = 1024;

/** Failed probes are retried rather than taken as final — see probeMimeType. */
let probeAttempts = 0;
const MAX_PROBE_ATTEMPTS = 3;

/**
 * Record ~400ms from a throwaway canvas in each candidate container and keep the
 * first that yields a plausible file.
 *
 * The probe carries a silent audio track because that is what breaks: the kiosk
 * encodes canvas video happily and falls over muxing AAC alongside it, so a
 * video-only probe would bless a container that fails in the real pipeline.
 *
 * Run it at warm-up, off the guest's critical path. It is idempotent and cheap,
 * and until it completes `pickMimeType()` keeps returning the static guess, so
 * nothing depends on it having finished.
 *
 * Bounded retries because a failure here is not necessarily a verdict on the
 * container — the warm-up runs it while the Camera Kit bootstrap is loading, and
 * under that load the same container that passed on one boot produced nothing on
 * the next.
 */
export async function probeMimeType(): Promise<string | null> {
  if (proven) return proven;
  if (probeAttempts >= MAX_PROBE_ATTEMPTS) return null;
  probeAttempts += 1;
  if (typeof MediaRecorder === 'undefined' || typeof HTMLCanvasElement === 'undefined') {
    return null;
  }

  const canvas = document.createElement('canvas');
  // 320x240 for 400ms clears a few KB in a working container; 128x128 did not,
  // and a pass/fail threshold needs headroom above the noise.
  canvas.width = 320;
  canvas.height = 240;
  const ctx = canvas.getContext('2d');
  let audio: AudioContext | undefined;

  /**
   * The list is an order of preference, and the probe must not quietly invert
   * it. A candidate can fail for reasons that have nothing to do with the
   * container — the main thread was busy and the probe got no paints — and on
   * 2026-09-18 that "proved" VP9 on the kiosk after VP8 came back empty, so
   * every clip cost twice the encode. The first supported candidate gets a
   * second, longer try before anything below it is believed.
   */
  const supported = candidates().filter((type) => MediaRecorder.isTypeSupported(type));
  const order = supported.length > 1 ? [supported[0]!, supported[0]!, ...supported.slice(1)] : supported;

  try {
    let tries = 0;
    for (const type of order) {
      tries += 1;
      const windowMs = tries === 2 ? 800 : 400;

      const stream = canvas.captureStream(15);
      try {
        // Silence, but a real audio track — see above.
        audio ??= new AudioContext();
        const dest = audio.createMediaStreamDestination();
        audio.createOscillator().connect(dest);
        dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));
      } catch {
        // No AudioContext — a video-only probe is still better than none.
      }

      const size = await new Promise<number>((resolve) => {
        let bytes = 0;
        let failed = false;
        let rec: MediaRecorder;
        try {
          rec = new MediaRecorder(stream, { mimeType: type });
        } catch {
          resolve(0);
          return;
        }
        rec.ondataavailable = (e) => {
          bytes += e.data.size;
        };
        rec.onerror = () => {
          failed = true;
        };
        rec.onstop = () => resolve(failed ? 0 : bytes);
        try {
          rec.start();
        } catch {
          resolve(0);
          return;
        }
        // Paint, so there is something to encode beyond a static frame — and
        // paint texture rather than flat colour, which any codec folds to a
        // few hundred bytes and leaves the pass mark down in the noise.
        let frames = 0;
        const paint = () => {
          if (!ctx) return;
          ctx.fillStyle = frames++ % 2 ? '#fff' : '#000';
          ctx.fillRect(0, 0, 320, 240);
          for (let i = 0; i < 40; i += 1) {
            ctx.fillStyle = `hsl(${(i * 37 + frames * 11) % 360} 80% 50%)`;
            ctx.fillRect((i * 53 + frames * 7) % 320, (i * 29 + frames * 3) % 240, 24, 24);
          }
          if (rec.state === 'recording') requestAnimationFrame(paint);
        };
        paint();
        window.setTimeout(() => {
          if (rec.state === 'recording') rec.stop();
          else resolve(failed ? 0 : bytes);
        }, windowMs);
      });

      stream.getTracks().forEach((t) => t.stop());
      if (size >= PLAUSIBLE_BYTES) {
        proven = type;
        return proven;
      }
      devWarn(`[recorder] probe: ${type} produced ${size} bytes in ${windowMs}ms — not proven`);
    }
    // Nothing passed. Leave `proven` unset so pickMimeType falls back to the
    // static list and a later attempt can still succeed.
    return null;
  } finally {
    void audio?.close().catch(() => {});
  }
}

/** The container the probe proved, or undefined if it has not proved one. */
export function provenMimeType(): string | undefined {
  return proven;
}

export function extensionFor(mimeType: string | null): string {
  if (!mimeType) return 'bin';
  if (mimeType.startsWith('video/mp4')) return 'mp4';
  if (mimeType.startsWith('video/webm')) return 'webm';
  return 'bin';
}

export function recorderSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.captureStream === 'function' &&
    pickMimeType() !== null
  );
}

/**
 * MMS will not carry a WebM. If the kiosk board turns out to be WebM-only we
 * need a server-side transcode step, so surface it rather than discover it on
 * show night.
 */
export function smsFriendly(mimeType: string | null): boolean {
  return Boolean(mimeType && mimeType.startsWith('video/mp4'));
}
